import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiBookOpen, 
  FiHelpCircle, 
  FiVideo, 
  FiShield,
  FiBook,
  FiBarChart2,
  FiUsers,
  FiMail
} from 'react-icons/fi';
import Heading from '../../ui/PageUI/Heading';
import Subheading from '../../ui/PageUI/SubHeading';
import Button from '../../components/Button';
import ImageWithText from '../../ui/PageUI/ImageWithText';
import imageSrc from '../../Assets/help.png';
import Paragraph from '../../ui/PageUI/Paragraph';
import Intro from '../../ui/PageUI/Intro';

const HelpCenter = () => {
  const mainSections = [
    {
      title: 'Getting Started',
      desc: 'New to Laani? Learn how to set up your account, secure your wallet, and make your first trade.',
      link: '/Help-Center/Getting-Started',
      icon: <FiBookOpen className="w-6 h-6" />,
      color: 'from-blue-600 to-blue-400'
    },
    {
      title: 'FAQs',
      desc: 'Have questions? Find answers to the most common inquiries about our platform and services.',
      link: '/Help-Center/FAQs',
      icon: <FiHelpCircle className="w-6 h-6" />,
      color: 'from-purple-600 to-purple-400'
    },
    {
      title: 'Tutorials',
      desc: 'Looking for detailed guides? Our tutorials will help you understand advanced trading strategies and platform features.',
      link: '/Help-Center/Tutorials',
      icon: <FiVideo className="w-6 h-6" />,
      color: 'from-teal-600 to-teal-400'
    }
  ];

  const additionalResources = [
    {
      title: 'Security Tips',
      link: '/Help-Center/Security-Tips',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Crypto Glossary',
      link: '/Help-Center/Glossary',
      icon: <FiBook className="w-5 h-5" />
    },
    {
      title: 'Market Insights',
      link: '/Help-Center/Market-Insights',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Customer Stories',
      link: '/Help-Center/Customer-Stories',
      icon: <FiUsers className="w-5 h-5" />
    }
  ];

  return (
    <div className="bg-gradient-to-b from-blue-900/20 to-transparent text-white min-h-screen">
      {/* Hero Section */}
      <div className="relative max-w-7xl mx-auto px-6 py-28 text-center">
        <div className="absolute inset-0 overflow-hidden opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl opacity-20"></div>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block px-4 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm font-medium mb-6 border border-blue-800/50">
            Support Center
          </div>
          <Heading 
            text="How Can We Help?" 
            className="text-4xl sm:text-5xl font-bold mb-4"
          />
          <Intro
            text="Welcome to the Laani Help Center. We're here to support you on your journey with cryptocurrencies. Whether you're just getting started, have questions, or want to dive deep into tutorials, we have resources to guide you every step of the way."
            className="text-gray-400 max-w-3xl mx-auto text-lg"
          />
        </motion.div>
      </div>

      {/* Main Sections */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-16">
          <Subheading 
            text="Explore our Help Center" 
            className="text-2xl text-gray-400 mb-2"
          />
          <p className="text-gray-500 max-w-2xl mx-auto">
            Quick access to our most popular help resources
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 mb-20">
          {mainSections.map((section, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              viewport={{ once: true, margin: "-100px" }}
              whileHover={{ y: -5 }}
            >
              <Link to={section.link}>
                <div className={`bg-gradient-to-br ${section.color} rounded-xl p-8 h-full border border-gray-700/50 hover:shadow-lg transition-all duration-300`}>
                  <div className="text-white mb-6">
                    {section.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{section.title}</h3>
                  <p className="text-gray-300 mb-6">{section.desc}</p>
                  <div className="text-sm font-medium text-white/80 hover:text-white transition-colors flex items-center">
                    Learn more <span className="ml-2">→</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Image + Text Section */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-gray-800/30 rounded-2xl border border-gray-700/50 overflow-hidden">
          <ImageWithText
            heading="Comprehensive Learning Resources"
            subheading="Your Crypto Education Hub"
            paragraph="At Laani, we prioritize your understanding and security in the crypto space. Our resources are designed to provide you with comprehensive information and guidance to ensure a safe and informed trading experience."
            bullets={mainSections.map(section => ({
              text: (
                <Link 
                  to={section.link} 
                  className="flex items-center gap-3 hover:text-blue-400 transition-colors"
                >
                  {section.icon}
                  {section.title}
                </Link>
              )
            }))}
            imageUrl={imageSrc}
            ctaText="Browse All Resources"
            ctaLink="/Help-Center"
          />
        </div>
      </div>

      {/* Commitment Section */}
      <div className="max-w-5xl mx-auto px-6 py-20 text-center">
        <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 rounded-2xl p-12 border border-gray-700/50">
          <div className="inline-block px-4 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm font-medium mb-6 border border-blue-800/50">
            Our Promise
          </div>
          <Heading 
            text="Empowering Your Crypto Journey" 
            className="text-3xl font-bold mb-6"
          />
          <Paragraph
            text="Laani is committed to providing a transparent, secure, and educational environment for all our users. We believe that informed users are empowered users, and we strive to deliver the highest quality resources and support to help you navigate the world of cryptocurrencies with confidence."
            className="text-gray-400 text-lg"
          />
        </div>
      </div>

      {/* Additional Resources */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-gradient-to-b from-blue-900/20 to-transparent rounded-2xl p-12 border border-gray-700/50">
          <div className="text-center mb-12">
            <Heading 
              text="Expand Your Knowledge" 
              className="text-3xl font-bold mb-4"
            />
            <p className="text-gray-400 max-w-2xl mx-auto">
              Stay informed and enhance your crypto expertise with our additional resources
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-10">
            {additionalResources.map((resource, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Link 
                  to={resource.link} 
                  className="flex items-center gap-3 p-4 rounded-lg bg-gray-700/50 hover:bg-gray-700/70 border border-gray-700/50 hover:border-blue-400/30 transition-all"
                >
                  <div className="text-blue-400">
                    {resource.icon}
                  </div>
                  <span className="font-medium">{resource.title}</span>
                </Link>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/Help-Center/More-Resources">
              <Button 
                text="View All Resources →" 
                className="bg-transparent border border-gray-600 hover:border-blue-400 text-blue-400 hover:text-blue-300"
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Support CTA */}
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <div className="bg-gray-800/30 rounded-2xl p-12 border border-gray-700/50">
          <div className="text-blue-400 text-4xl mb-6">
            <FiMail className="inline-block" />
          </div>
          <Heading 
            text="Need More Help?" 
            className="text-3xl font-bold mb-4"
          />
          <p className="text-gray-400 mb-8">
            Our support team is available 24/7 to assist you with any questions or issues.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/ContactUs">
              <Button 
                text="Contact Support" 
                className="bg-blue-600 hover:bg-blue-700"
              />
            </Link>
            <Link to="/blogs">
              <Button 
                text="Visit Our Blog →" 
                className="bg-transparent border border-gray-600 hover:border-blue-400"
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;



